import React from 'react';
import * as Yup from 'yup';
import { FiLock } from 'react-icons/fi';

import Input from '../../../../components/Forms/Input';
import MaskedInput from '../../../../components/Forms/MaskedInput';
import SelectSimple from '../../../../components/Forms/SelectSimple';
import CheckboxInput from '../../../../components/Forms/CheckboxInput';

import { countries } from './countries';
// Se você tiver o states.js do módulo Login, importe. Caso contrário, remova o campo rg_uf.
// import states from './states';

/* =========================================================================
 * Contexto compartilhado pelos campos.
 * Tudo que cada campo precisa para decidir como renderizar ou validar
 * passa por aqui.
 * ========================================================================= */
export interface FieldContext {
  countryISO: string;
  translate: (k: string) => string;
  formRef: any;
  onCepFetch?: (cep: string) => Promise<void>;
  setCountryISO?: (iso: string) => void;

  /** Usuário logado (null em cadastro novo). Útil em `when` para esconder campos já preenchidos. */
  user?: Record<string, any> | null;

  /** Estado reativo dos campos observados (via setCallback). Use em `when`. */
  values: Record<string, any>;

  /** Atualiza `values`. Os `render` de campos condicionais disparadores devem chamar isto no setCallback. */
  setValue: (name: string, value: any) => void;

  /** Dados arbitrários de contexto (ex: projectHash, eventHash). */
  extras?: Record<string, any>;
}

/* =========================================================================
 * Definição de um campo.
 * - render:     JSX do campo (pode variar por país, etc.)
 * - validation: schema Yup do campo (retorne null p/ opcional sem regra)
 * - group:      agrupamento p/ títulos de seção (opcional)
 * - normalize:  transforma o valor antes do submit (ex.: remover máscara)
 * ========================================================================= */
export interface FieldDef {
  name: string;
  group?: string;
  render: (ctx: FieldContext) => React.ReactNode;
  validation?: (ctx: FieldContext) => any | null;
  normalize?: (value: any, ctx: FieldContext) => any;

  /**
   * Se retornar `false`, o campo é escondido, não validado e removido do payload.
   * Recebe o ctx completo (incluindo `values` reativo e `user`).
   */
  when?: (ctx: FieldContext) => boolean;
}

/* =========================================================================
 * Helpers para mensagens de erro (padronizados com o estilo do projeto).
 * ========================================================================= */
const msg = (t: FieldContext['translate'], campo: string, suffix = 'obrigatório') =>
  `${t(campo)} ${t(suffix)}`;

/* =========================================================================
 * CATÁLOGO DE CAMPOS
 * Adicione novos campos aqui. Para usá-los, basta colocar o `name` no array
 * `fields` passado ao componente CadastroUnico.
 * ========================================================================= */
export const REGISTRATION_FIELDS: Record<string, FieldDef> = {
  /* ---------- Identificação / País ---------- */
  country: {
    name: 'country',
    group: 'personal',
    render: ({ translate, setCountryISO }) => (
      <SelectSimple style={{width: '100%'}}
        setCallback={(v: string) => setCountryISO?.(v)}
        options={countries}
        name="country"
        label={translate('Pais')}
      />
    ),
  },

  /* ---------- Dados pessoais ---------- */
  name: {
    name: 'name',
    group: 'personal',
    // Esconde quando usuário logado já tem nome (cenário do AtualizarCadastro)
  
    render: ({ translate }) => <Input name="name" placeholder={translate('Nome completo')} />,
    validation: ({ translate, user }) =>
      user?.name ? null : Yup.string().required(msg(translate, 'Nome')),
  },

  social_name: {
    name: 'social_name',
    group: 'personal',
    render: ({ translate }) => <Input name="social_name" placeholder={translate('Nome Social')} />,
  },

  email: {
    name: 'email',
    group: 'auth',
    render: ({ translate }) => <Input name="email" placeholder={translate('E-mail')} />,
    validation: ({ translate }) =>
      Yup.string().email(msg(translate, 'Email')).required(msg(translate, 'Email')),
  },

  password: {
    name: 'password',
    group: 'auth',
    render: ({ translate }) => (
      <Input
        icon={FiLock}
        name="password"
        type="password"
        placeholder={translate('Cadastre sua senha de acesso')}
      />
    ),
    validation: ({ translate }) =>
      Yup.string().required(msg(translate, 'Senha', 'obrigatória')),
  },

  phone: {
    name: 'phone',
    group: 'personal',
    render: ({ translate, countryISO }) =>
      countryISO === 'BR' ? (
        <MaskedInput
          mask="(99) 99999.9999"
          name="phone"
          placeholder={translate('Telefone / Whatsapp')}
        />
      ) : (
        <Input name="phone" placeholder={translate('Telefone')} />
      ),
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Telefone')),
    normalize: (v: any) => (v ? String(v).replace(/[^0-9]/g, '') : v),
  },

  document_type: {
    name: 'document_type',
    group: 'personal',
    render: ({ translate, countryISO }) =>
      countryISO === 'BR' ? (
        <Input
          style={{ display: 'none' }}
          name="document_type"
          value="cpf"
          placeholder={translate('Tipo de documento')}
        />
      ) : (
        <SelectSimple style={{width: '100%'}}
          name="document_type"
          label={translate('Tipo de Documento (Passaporte / Outro)')}
          options={[
            { label: translate('Passaporte'), value: 'passport' },
            { label: translate('Outro'), value: 'other' },
          ]}
        />
      ),
  },

  document_number: {
    name: 'document_number',
    group: 'personal',
    render: ({ translate, countryISO }) =>
      countryISO === 'BR' ? (
        <MaskedInput
          mask="999.999.999-99"
          name="document_number"
          placeholder={translate('CPF')}
        />
      ) : (
        <Input name="document_number" placeholder={translate('Nº do documento')} />
      ),
    validation: ({ translate, countryISO }) => {
      const base = Yup.string().required(msg(translate, 'CPF'));
      return countryISO === 'BR' ? base.min(11, msg(translate, 'CPF')) : base;
    },
    normalize: (v: any) => (v ? String(v).replace(/[^0-9]/g, '') : v),
  },

  birth_date: {
    name: 'birth_date',
    group: 'personal',
    render: ({ translate }) => (
      <MaskedInput
        mask="99/99/9999"
        name="birth_date"
        placeholder={translate('Data de Nascimento (DD/MM/AAAA)')}
      />
    ),
    validation: ({ translate }) =>
      Yup.string()
        .min(10, translate('min 10 Preecha a Data de Nascimento'))
        .max(10, translate('max 10 Preecha a Data de Nascimento'))
        .required(msg(translate, 'Data de Nascimento')),
  },

  gender_id: {
    name: 'gender_id',
    group: 'personal',
    render: ({ translate }) => (
      <SelectSimple style={{width: '100%'}}  index={20}
        options={[
          { value: 'Masculino', label: 'Masculino', text: 'Masculino' },
          { value: 'Feminino', label: 'Feminino', text: 'Feminino' },
          { value: 'Prefiro não informar', label: 'Prefiro não informar', text: 'Prefiro não informar' },
        ]}
        name="gender_id"
        label={translate('Gênero')}
      />
    ),
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Gênero')),
  },
 instituicao: {
    name: 'instituicao',            // precisa bater com o nome esperado pelo backend (/signup e /update-user)
    group: 'personal',              // aparece na seção "Dados pessoais"
    render: ({ translate }) => (
      <Input name="instituicao" placeholder={translate('Instituição onde concluiu a graduação')} />
    ),
    // opcional — só se for obrigatório:
    validation: ({ translate }) =>
      Yup.string().required(msg(translate, 'Instituição onde concluiu a graduação')),
  },
  rg: {
    name: 'rg',
    group: 'personal',
    render: ({ translate }) => (
      <Input name="rg" placeholder={translate('Carteira de Identidade - RG')} />
    ),
    validation: ({ translate }) =>
      Yup.string().required(msg(translate, 'Carteira de Identidade (RG)')),
  },

  mother_name: {
    name: 'mother_name',
    group: 'personal',
    render: ({ translate }) => (
      <Input name="mother_name" placeholder={translate('Nome completo da Mãe')} />
    ),
    validation: ({ translate }) =>
      Yup.string().required(msg(translate, 'Nome completo da mãe')),
  },

  father_name: {
    name: 'father_name',
    group: 'personal',
    render: ({ translate }) => (
      <Input name="father_name" placeholder={translate('Nome completo do Pai')} />
    ),
  },

  nationality: {
    name: 'nationality',
    group: 'personal',
    render: ({ translate, formRef }) =>   <SelectSimple style={{width: '100%'}}  index={30}
        options={[
          { label: 'BRASILEIRA', value: 'BRASILEIRA' },
          { label: 'ESTRANGEIRA', value: 'ESTRANGEIRA' },
           
        ]}
        name="nationality"
        formRef={formRef}
        label={translate('Nacionalidade')}
       
      /> ,
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Nacionalidade')),
  },

  born_in: {
    name: 'born_in',
    group: 'personal',
    render: ({ translate }) => <Input name="born_in" placeholder={translate('Naturalidade')} />,
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Naturalidade')),
  },

  /* ---------- Endereço ---------- */
  zipcode: {
    name: 'zipcode',
    group: 'address',
    render: ({ translate, countryISO, onCepFetch }) =>
      countryISO === 'BR' ? (
        <MaskedInput
          setCallback={onCepFetch}
          mask="99999999"
          name="zipcode"
          placeholder={translate('CEP')}
        />
      ) : (
        <Input name="zipcode" placeholder={translate('ZIP Code')} />
      ),
    validation: ({ translate, countryISO }) => {
      const base = Yup.string().required(msg(translate, 'CEP'));
      return countryISO === 'BR'
        ? base.min(8, msg(translate, 'CEP')).max(8, msg(translate, 'CEP'))
        : base;
    },
    normalize: (v: any, ctx) =>
      ctx.countryISO === 'BR' && v ? String(v).replace(/[^0-9]/g, '') : v,
  },

  address: {
    name: 'address',
    group: 'address',
    render: ({ translate }) => <Input name="address" placeholder={translate('Endereço')} />,
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Endereço')),
  },

  address_number: {
    name: 'address_number',
    group: 'address',
    render: ({ translate }) => (
      <Input name="address_number" placeholder={translate('Número / Apartamento')} />
    ),
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Número')),
  },

  address_number_extra: {
    name: 'address_number_extra',
    group: 'address',
    render: ({ translate }) => (
      <Input name="address_number_extra" placeholder={translate('Complemento')} />
    ),
  },

  neighborhood: {
    name: 'neighborhood',
    group: 'address',
    render: ({ translate }) => <Input name="neighborhood" placeholder={translate('Bairro')} />,
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Bairro')),
  },

  city: {
    name: 'city',
    group: 'address',
    render: ({ translate }) => <Input name="city" placeholder={translate('Cidade')} />,
    validation: ({ translate }) => Yup.string().required(msg(translate, 'Cidade')),
  },

  state: {
    name: 'state',
    group: 'address',
    render: ({ translate, countryISO }) =>
      countryISO === 'BR' ? (
        <MaskedInput mask="aa" name="state" placeholder={translate('Estado')} />
      ) : (
        <Input name="state" placeholder={translate('Província / Região')} />
      ),
    validation: ({ translate, countryISO }) => {
      const base = Yup.string().required(msg(translate, 'Estado'));
      return countryISO === 'BR'
        ? base.max(2, translate('No máximo 2 caracteres'))
        : base;
    },
  },

  /* ---------- Empresa (PJ) ---------- */
  company: {
    name: 'company',
    group: 'company',
    render: ({ translate }) => <Input name="company" placeholder={translate('Empresa')} />,
  },

  position: {
    name: 'position',
    group: 'company',
    render: ({ translate }) => <Input name="position" placeholder={translate('Cargo')} />,
  },

  company_document_number: {
    name: 'company_document_number',
    group: 'company',
    render: ({ translate, countryISO }) =>
      countryISO === 'BR' ? (
        <MaskedInput
          mask="99.999.999/9999-99"
          name="company_document_number"
          placeholder={translate('CNPJ da Empresa / Entidade')}
        />
      ) : (
        <Input
          name="company_document_number"
          placeholder={translate('Nº de identificação da Empresa / Entidade')}
        />
      ),
    normalize: (v: any) => (v ? String(v).replace(/[^0-9]/g, '') : v),
  },

  /* ---------- Como conheceu ---------- */
  know_about: {
    name: 'know_about',
    group: 'extra',
    render: ({ translate, formRef, setValue }) => (
      <SelectSimple style={{width: '100%'}}
        options={[
          { label: 'WhatsApp', value: 'WhatsApp' },
          { label: translate('Indicação'), value: 'Indicação' },
          { label: 'Instagram', value: 'Instagram' },
          { label: 'Facebook', value: 'Facebook' },
          { label: 'Linkedin', value: 'Linkedin' },
          { label: translate('E-mail marketing'), value: 'E-mail marketing' },
          { label: 'SMS', value: 'SMS' },
          { label: 'Google', value: 'Google' },
          { label: translate('Outra'), value: 'Outra' },
        ]}
        name="know_about"
        formRef={formRef}
        label={translate('Como você chegou a esta página?')}
       
      />
    ),
  },

  /** Aparece apenas quando know_about === 'Outra' */
  know_about_other: {
    name: 'know_about_other',
    group: 'extra',
    when: ({ values }) => values?.know_about === 'Outra',
    render: ({ translate }) => (
      <Input name="know_about_other" placeholder={translate('Qual?')} />
    ),
    validation: ({ translate, values }) =>
      values?.know_about === 'Outra'
        ? Yup.string().required(msg(translate, 'Como nos conheceu'))
        : null,
  },

  /* ---------- Termos / Comunicações ---------- */
  term: {
    name: 'term',
    group: 'terms',
    render: ({ translate }) => (
      <CheckboxInput
        style={{ display: 'flex' }}
        name="term"
        unique={true}
        preText={<>{translate('Aceito receber comunicações por e-mail e SMS?')}</>}
        options={[
          { id: 'Sim', label: translate('Sim'), value: 'Sim' },
          { id: 'Não', label: translate('Não'), value: 'Não' },
        ]}
      />
    ),
  },
};

/* =========================================================================
 * Helpers: monta schema + roda normalizações para um dado array de campos.
 * Campos com `when()` que retornam false são ignorados (não validados,
 * não normalizados e removidos do payload).
 * ========================================================================= */
export function isFieldVisible(name: string, ctx: FieldContext): boolean {
  const def = REGISTRATION_FIELDS[name];
  if (!def) return false;
  return def.when ? def.when(ctx) : true;
}

export function getVisibleFields(fieldNames: string[], ctx: FieldContext): string[] {
  return fieldNames.filter(n => isFieldVisible(n, ctx));
}

export function buildSchema(fieldNames: string[], ctx: FieldContext) {
  const shape: Record<string, any> = {};
  getVisibleFields(fieldNames, ctx).forEach(name => {
    const def = REGISTRATION_FIELDS[name];
    if (!def?.validation) return;
    const rule = def.validation(ctx);
    if (rule) shape[name] = rule;
  });
  return Yup.object().shape(shape);
}

export function normalizeData(
  data: Record<string, any>,
  fieldNames: string[],
  ctx: FieldContext,
) {
  const visible = new Set(getVisibleFields(fieldNames, ctx));
  const out: Record<string, any> = {};

  // copia apenas campos visíveis + aplica normalizações
  fieldNames.forEach(name => {
    if (!visible.has(name)) return;
    const def = REGISTRATION_FIELDS[name];
    let v = data[name];
    if (def?.normalize && v !== undefined) {
      v = def.normalize(v, ctx);
    }
    if (v !== undefined) out[name] = v;
  });

  // preserva campos do form que não estão no catálogo (ex: captcha injetado em onSubmit)
  Object.keys(data).forEach(k => {
    if (!(k in REGISTRATION_FIELDS) && !(k in out)) {
      out[k] = data[k];
    }
  });

  return out;
}
